<!--
  A frecency-ordered combobox over the ledger's merchant directory, doubling as free text:
  there is no `merchantId` on `PostEntryRequest` (contract/openapi.json), only `payee` - a
  merchant is a side effect of that string (myWealth spec §1.1, "findOrCreateByName"), so
  the model here is always the payee text itself, never an id. Picking a result fills it
  with that merchant's name; typing something new and committing it is exactly how a new
  merchant comes into existence on save. Same ARIA combobox pattern and teleported popover
  as AccountCombobox.vue - see its own comment for why.
-->
<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { frecencyWhen, type Frecency } from '../internal/targets.ts'
import { matchesMerchantQuery, rankMerchants, type Merchant } from '../internal/merchant-targets.ts'

const props = defineProps<{
  id: string
  label: string
  modelValue: string | undefined
  merchants: readonly Merchant[]
  frecency: ReadonlyMap<string, Frecency>
}>()

const emit = defineEmits<{
  'update:modelValue': [string | undefined]
  accept: []
  select: [Merchant]
  /** The typed text, debounced - lets a caller search the full directory server-side
   * instead of relying on whatever page `merchants` already holds. */
  query: [string]
}>()

const QUERY_DEBOUNCE_MS = 400

const { t } = useI18n()

function frecencyHint(frecency: Frecency): string {
  const when = frecencyWhen(frecency)
  const whenText =
    when === 'today'
      ? t('entries.frecencyToday')
      : when === 'yesterday'
        ? t('entries.frecencyYesterday')
        : when === 'daysAgo'
          ? t('entries.frecencyDaysAgo', { count: frecency.lastUsedDaysAgo })
          : undefined
  return whenText === undefined
    ? t('entries.frecencyCount', { count: frecency.count })
    : t('entries.frecencyCountWhen', { count: frecency.count, when: whenText })
}

const inputRef = useTemplateRef<HTMLInputElement>('input')
const query = ref(props.modelValue ?? '')
const isOpen = ref(false)
const highlightIndex = ref(0)
const popoverStyle = ref<{ top: string; left: string; width: string }>({
  top: '0px',
  left: '0px',
  width: '0px',
})

function updatePopoverPosition(): void {
  const rect = inputRef.value?.getBoundingClientRect()
  if (rect === undefined) return
  popoverStyle.value = {
    top: `${rect.bottom + 2}px`,
    left: `${rect.left}px`,
    width: `${Math.max(rect.width, 288)}px`,
  }
}

watch(
  () => props.modelValue,
  (value) => {
    if (value !== query.value) query.value = value ?? ''
  },
)

const ranked = computed(() => rankMerchants(props.merchants, props.frecency))
const filtered = computed(() =>
  ranked.value.filter((entry) => matchesMerchantQuery(entry.merchant, query.value)),
)
const mostUsed = computed(() => filtered.value.filter((entry) => entry.frecency !== undefined))
const rest = computed(() => filtered.value.filter((entry) => entry.frecency === undefined))
const flatResults = computed(() => [...mostUsed.value, ...rest.value])

function optionId(merchantId: string): string {
  return `${props.id}-option-${merchantId}`
}

function onReposition(): void {
  if (isOpen.value) updatePopoverPosition()
}
window.addEventListener('scroll', onReposition, true)
window.addEventListener('resize', onReposition)

let queryDebounceTimer: ReturnType<typeof setTimeout> | undefined
function emitQueryDebounced(): void {
  clearTimeout(queryDebounceTimer)
  queryDebounceTimer = setTimeout(() => emit('query', query.value.trim()), QUERY_DEBOUNCE_MS)
}

// This component remounts fresh on every popover open (the filter panel is `v-if`),
// so a mount-time emit is a reopen reset for a caller searching the server.
onMounted(() => emit('query', query.value))

onUnmounted(() => {
  window.removeEventListener('scroll', onReposition, true)
  window.removeEventListener('resize', onReposition)
  clearTimeout(queryDebounceTimer)
})

function openList(): void {
  updatePopoverPosition()
  isOpen.value = true
  highlightIndex.value = 0
}

function closeList(): void {
  isOpen.value = false
}

function highlightParts(name: string): { before: string; match: string; after: string } {
  const needle = query.value.trim()
  if (needle === '') return { before: name, match: '', after: '' }
  const index = name.toLowerCase().indexOf(needle.toLowerCase())
  if (index === -1) return { before: name, match: '', after: '' }
  return {
    before: name.slice(0, index),
    match: name.slice(index, index + needle.length),
    after: name.slice(index + needle.length),
  }
}

function commit(value: string): void {
  const trimmed = value.trim()
  emit('update:modelValue', trimmed === '' ? undefined : trimmed)
}

function select(merchant: Merchant): void {
  query.value = merchant.name
  commit(merchant.name)
  emit('select', merchant)
  closeList()
}

function acceptHighlighted(): boolean {
  const merchant = flatResults.value[highlightIndex.value]?.merchant
  if (merchant === undefined) return false
  select(merchant)
  return true
}

function onInput(): void {
  openList()
  emitQueryDebounced()
}

function onFocus(): void {
  openList()
  void nextTick(() => inputRef.value?.select())
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (!isOpen.value) return openList()
    highlightIndex.value = Math.min(highlightIndex.value + 1, flatResults.value.length - 1)
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    highlightIndex.value = Math.max(highlightIndex.value - 1, 0)
  } else if (event.key === 'Enter') {
    if (isOpen.value && acceptHighlighted()) {
      event.preventDefault()
    } else {
      commit(query.value)
      closeList()
    }
    emit('accept')
  } else if (event.key === 'Tab') {
    if (isOpen.value && !acceptHighlighted()) commit(query.value)
    closeList()
  } else if (event.key === 'Escape') {
    event.preventDefault()
    query.value = props.modelValue ?? ''
    closeList()
  }
}

// Typing a value that matches nothing and clicking away still commits it as free text -
// that new payee is exactly how a merchant comes into existence on save (myWealth spec).
function onBlur(): void {
  commit(query.value)
  closeList()
}

defineExpose({ focus: () => inputRef.value?.focus() })
</script>

<template>
  <div class="relative">
    <input
      :id="id"
      ref="input"
      v-model="query"
      type="text"
      role="combobox"
      autocomplete="off"
      :aria-label="label"
      aria-autocomplete="list"
      :aria-expanded="isOpen"
      :aria-controls="`${id}-listbox`"
      :aria-activedescendant="
        isOpen && flatResults[highlightIndex] ? optionId(flatResults[highlightIndex]!.merchant.id) : undefined
      "
      class="min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
    />
    <Teleport to="body">
      <ul
        v-if="isOpen && flatResults.length > 0"
        :id="`${id}-listbox`"
        role="listbox"
        :aria-label="label"
        class="fixed z-20 max-h-72 overflow-y-auto rounded-[var(--radius-overlay)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] py-[var(--space-tight)] shadow-[var(--shadow-overlay)]"
        :style="popoverStyle"
      >
        <template v-for="(entry, index) in flatResults" :key="entry.merchant.id">
          <li
            v-if="index === 0 && mostUsed.length > 0"
            role="presentation"
            class="px-[var(--space-cell)] py-[var(--space-tight)] text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            {{ t('entries.mostUsed') }}
          </li>
          <li
            v-if="index === mostUsed.length && rest.length > 0"
            role="presentation"
            class="px-[var(--space-cell)] py-[var(--space-tight)] text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            {{ t('entries.everythingElse') }}
          </li>
          <li
            :id="optionId(entry.merchant.id)"
            role="option"
            :aria-selected="index === highlightIndex"
            class="flex cursor-pointer items-baseline justify-between gap-[var(--space-cell)] px-[var(--space-cell)] py-[var(--space-inline)]"
            :class="index === highlightIndex ? 'bg-[color:var(--color-surface-raised)]' : undefined"
            @mousedown.prevent="select(entry.merchant)"
            @mouseenter="highlightIndex = index"
          >
            <span class="min-w-0 truncate text-[color:var(--color-text)]">
              <span>{{ highlightParts(entry.merchant.name).before }}</span
              ><span class="text-[color:var(--color-accent)]">{{
                highlightParts(entry.merchant.name).match
              }}</span
              ><span>{{ highlightParts(entry.merchant.name).after }}</span>
            </span>
            <span
              v-if="entry.frecency !== undefined"
              class="shrink-0 whitespace-nowrap text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ frecencyHint(entry.frecency) }}
            </span>
          </li>
        </template>
      </ul>
    </Teleport>
  </div>
</template>
